import { getRouteApi, useNavigate } from "@tanstack/react-router";
import { useCallback, useMemo, useState } from "react";
import { ApiError, useExport, useFacets, useSites } from "../lib/api";
import { runExport } from "../lib/download";
import { fmtInt, fmtMw } from "../lib/format";
import {
  DEFAULTS,
  filterPart,
  type Search,
  type SearchPatch,
  withTierDefault,
} from "../lib/search";
import { usePersisted } from "../lib/theme";
import { AddToListDialog } from "../ui/AddToListDialog";
import { ColumnChooser, DataTable } from "../ui/DataTable";
import { FilterRail } from "../ui/FilterRail";
import { DEFAULT_SITE_COLUMNS, SITE_COLUMNS } from "../ui/siteColumns";
import { useToast } from "../ui/Toast";

const route = getRouteApi("/table");

export function TablePage() {
  const search = route.useSearch();
  const navigate = useNavigate({ from: "/table" });
  const toast = useToast();
  const patch = useCallback(
    (p: SearchPatch) => void navigate({ search: (prev) => ({ ...prev, ...p }) }),
    [navigate],
  );
  const reset = useCallback(
    () =>
      void navigate({
        search: () => ({
          mw_min: DEFAULTS.mw_min,
          mw_max: DEFAULTS.mw_max,
          page: 1,
          per_page: search.per_page,
        }),
      }),
    [navigate, search.per_page],
  );

  const facets = useFacets();
  const hasScores = facets.data?.has_scores ?? false;
  const filters = useMemo(
    () => ({
      ...withTierDefault(filterPart(search), hasScores),
      sort: search.sort,
      dir: search.dir,
      page: search.page,
      per_page: search.per_page,
    }),
    [search, hasScores],
  );
  const q = useSites(filters);
  const [visible, setVisible] = usePersisted<string[]>("table:columns", DEFAULT_SITE_COLUMNS);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [dialogIds, setDialogIds] = useState<number[] | null>(null);
  const exp = useExport();

  const rows = q.data?.items ?? [];
  const total = q.data?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / search.per_page));
  const sort = search.sort
    ? {
        key: search.sort,
        dir:
          search.dir ??
          (search.sort === "name" || search.sort === "state" || search.sort === "operator"
            ? "asc"
            : "desc"),
      }
    : undefined;

  const onSort = (key: string) => {
    if (search.sort === key)
      patch({ dir: (search.dir ?? "desc") === "desc" ? "asc" : "desc", page: 1 });
    else
      patch({
        sort: key as Search["sort"],
        dir: key === "name" || key === "state" || key === "operator" ? "asc" : "desc",
        page: 1,
      });
  };
  const toggle = (id: number) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const toggleAll = (ids: number[]) =>
    setSelected((prev) => {
      const all = ids.every((id) => prev.has(id));
      const next = new Set(prev);
      for (const id of ids) all ? next.delete(id) : next.add(id);
      return next;
    });

  const doExport = (format: "csv" | "geojson") =>
    runExport(
      exp.mutateAsync,
      selected.size > 0
        ? { site_ids: [...selected], format, columns: "gary" }
        : {
            filter: withTierDefault(filterPart(search), hasScores) as Record<string, unknown>,
            format,
            columns: "gary",
          },
      toast,
    );

  return (
    <div className="flex h-full min-h-0">
      <FilterRail search={search} patch={patch} reset={reset} showMapControls={false} />
      <div className="flex min-w-0 flex-1 flex-col">
        <div
          className="flex flex-wrap items-center gap-3 border-b px-3 py-2"
          style={{ borderColor: "var(--line)", background: "var(--panel)" }}
        >
          <div className="num text-[12px]" data-testid="table-summary">
            {q.isLoading
              ? "Loading…"
              : `${fmtInt(total)} sites · ${fmtInt(q.data?.summary.units ?? 0)} units · ${fmtMw(q.data?.summary.mw ?? 0)} MW`}
            {q.error ? (
              <span style={{ color: "var(--danger)" }}>
                {" "}
                · {q.error instanceof ApiError ? q.error.message : "error"}
              </span>
            ) : null}
          </div>
          <div className="ml-auto flex items-center gap-2">
            {selected.size > 0 ? (
              <>
                <span className="text-[12px]" style={{ color: "var(--muted)" }}>
                  {selected.size} selected
                </span>
                <button
                  type="button"
                  className="btn btn-sm"
                  onClick={() => setDialogIds([...selected])}
                  data-testid="bulk-add-to-list"
                >
                  Add to list
                </button>
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  onClick={() => setSelected(new Set())}
                >
                  Clear
                </button>
              </>
            ) : (
              <button
                type="button"
                className="btn btn-sm"
                onClick={() => setDialogIds(rows.map((r) => r.id))}
                disabled={rows.length === 0}
              >
                Add page to list
              </button>
            )}
            <button
              type="button"
              className="btn btn-sm"
              onClick={() => doExport("csv")}
              disabled={exp.isPending || total === 0}
            >
              Export CSV
            </button>
            <button
              type="button"
              className="btn btn-sm"
              onClick={() => doExport("geojson")}
              disabled={exp.isPending || total === 0}
            >
              GeoJSON
            </button>
            <ColumnChooser columns={SITE_COLUMNS} visible={visible} onChange={setVisible} />
          </div>
        </div>
        <div className="scroll-thin min-h-0 flex-1 overflow-auto">
          <DataTable
            columns={SITE_COLUMNS}
            visible={visible}
            rows={rows}
            rowKey={(r) => r.id}
            sort={sort}
            onSort={onSort}
            selected={selected}
            onToggle={toggle}
            onToggleAll={toggleAll}
            emptyText={q.isLoading ? "Loading…" : "No sites match these filters."}
            testId="sites-table"
          />
        </div>
        <div
          className="flex items-center gap-3 border-t px-3 py-1.5 text-[12px]"
          style={{ borderColor: "var(--line)", background: "var(--panel)" }}
        >
          <button
            type="button"
            className="btn btn-sm"
            disabled={search.page <= 1}
            onClick={() => patch({ page: search.page - 1 })}
          >
            ‹ Prev
          </button>
          <span className="num">
            page {search.page} of {pages}
          </span>
          <button
            type="button"
            className="btn btn-sm"
            disabled={search.page >= pages}
            onClick={() => patch({ page: search.page + 1 })}
          >
            Next ›
          </button>
          <label className="ml-auto flex items-center gap-1" style={{ color: "var(--muted)" }}>
            rows
            <select
              className="input"
              value={search.per_page}
              onChange={(e) => patch({ per_page: Number(e.target.value), page: 1 })}
              aria-label="Rows per page"
            >
              {[25, 50, 100, 200].map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </label>
        </div>
      </div>
      <AddToListDialog
        open={dialogIds !== null}
        siteIds={dialogIds ?? []}
        onClose={() => setDialogIds(null)}
        onDone={(_id, name) => {
          toast(`Added ${dialogIds?.length ?? 0} sites to ${name}`);
          setSelected(new Set());
        }}
      />
    </div>
  );
}
